<template>
  <div class="nav-bar-operations">
    <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" />
  </div>

  <main class="page">
    <header class="page-header">
      <div class="titles">
        <h2>Mis incidencias</h2>
        <p class="subtitle">Consulta las incidencias registradas a tu nombre y su estatus.</p>
      </div>

      <div class="user-actions">
        <div class="welcome">
          <span class="chip">👤</span>
          <div class="welcome-text">
            <div class="welcome-label">Bienvenido/a</div>
            <div class="welcome-name">{{ firstName }}</div>
          </div>
        </div>

        <div class="action-buttons">
          <button class="btn btn-ghost" @click="goBack">Regresar</button>
          <button class="btn btn-danger" @click="logout">Cerrar sesión</button>
        </div>
      </div>
    </header>

    <section class="card">
      <div class="card-header">
        <div>
          <h3>Resultados</h3>
          <p class="muted">
            Mostrando {{ paginatedIncidents.length }} de {{ incidents.length }} incidencias
          </p>
        </div>

        <div class="pager inline-pager">
          <button class="icon-btn" @click="prevPage" :disabled="currentPage === 1" aria-label="Página anterior">
            <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
          </button>
          <span class="pager-label">Página <b>{{ currentPage }}</b> de <b>{{ totalPages }}</b></span>
          <button class="icon-btn" @click="nextPage" :disabled="currentPage >= totalPages" aria-label="Página siguiente">
            <img src="@/assets/angulo-derecho.png" alt="Next Page" />
          </button>
        </div>
      </div>

      <!-- Desktop / tablet table -->
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>Ocurrencia</th>
              <th>Nivel</th>
              <th>Tipo</th>
              <th>Estatus</th>
              <th>Descripción</th>
              <th>Acción correctiva</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="incident in paginatedIncidents" :key="incident.id" class="row-click" @click="openDetail(incident)">
              <td class="mono">{{ formatDate(incident.occurrence_time) }}</td>
              <td>{{ levelLabel(incident.level) }}</td>
              <td>{{ incident.type || '—' }}</td>
              <td>
                <span class="status-pill" :class="incidentStatusClass(incident.status)">
                  {{ incident.status || 'En revision' }}
                </span>
              </td>
              <td class="wrap">{{ incident.description || '—' }}</td>
              <td class="wrap">{{ incident.punishment_description || '—' }}</td>
            </tr>

            <tr v-if="paginatedIncidents.length === 0">
              <td colspan="6" class="empty">
                No tienes incidencias registradas.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="mobile-list">
        <article class="mobile-item" v-for="incident in paginatedIncidents" :key="'m'+incident.id" @click="openDetail(incident)">
          <div class="mobile-top">
            <div class="left">
              <span class="id-pill">{{ incident.type || 'Incidencia' }}</span>
            </div>
            <div class="right-col">
              <span class="status-pill" :class="incidentStatusClass(incident.status)">
                {{ incident.status || 'En revision' }}
              </span>
              <div class="mono muted">{{ formatDate(incident.occurrence_time) }}</div>
            </div>
          </div>

          <div class="mobile-grid">
            <div class="mobile-block">
              <span class="label">Nivel</span>
              <div>{{ levelLabel(incident.level) }}</div>
            </div>
            <div class="mobile-block">
              <span class="label">Descripción</span>
              <div class="wrap">{{ incident.description || '—' }}</div>
            </div>
            <div class="mobile-block">
              <span class="label">Acción correctiva</span>
              <div class="wrap">{{ incident.punishment_description || '—' }}</div>
            </div>
          </div>
        </article>

        <div v-if="paginatedIncidents.length === 0" class="mobile-empty">
          No tienes incidencias registradas.
        </div>
      </div>

      <div class="totals-bar">
        <div class="totals-item">
          <span class="label">Total de incidencias</span>
          <span class="value">{{ incidents.length }}</span>
        </div>
        <div class="totals-item highlight">
          <span class="label">En revisión</span>
          <span class="value">{{ inReviewCount }}</span>
        </div>
      </div>

      <div class="pager sticky-pager">
        <button class="icon-btn" @click="prevPage" :disabled="currentPage === 1" aria-label="Página anterior">
          <img src="@/assets/angulo-izquierdo.png" alt="Previous Page" />
        </button>
        <span class="pager-label">Página <b>{{ currentPage }}</b> de <b>{{ totalPages }}</b></span>
        <button class="icon-btn" @click="nextPage" :disabled="currentPage >= totalPages" aria-label="Página siguiente">
          <img src="@/assets/angulo-derecho.png" alt="Next Page" />
        </button>
      </div>
    </section>

    <div v-if="showDetailModal && selectedIncident" class="modal-backdrop" @click.self="closeDetail">
      <div class="modal-card">
        <div class="modal-head">
          <h4>Incidencia #{{ selectedIncident.id }}</h4>
          <button type="button" class="btn-close" @click="closeDetail">✕</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-field">
              <span class="detail-label">Fecha de ocurrencia</span>
              <span class="detail-value">{{ formatDate(selectedIncident.occurrence_time) }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">Nivel</span>
              <span class="detail-value">{{ levelLabel(selectedIncident.level) }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">Tipo</span>
              <span class="detail-value">{{ selectedIncident.type || '—' }}</span>
            </div>
            <div class="detail-field">
              <span class="detail-label">Estatus</span>
              <span class="status-pill" :class="incidentStatusClass(selectedIncident.status)">
                {{ selectedIncident.status || 'En revision' }}
              </span>
            </div>
            <div class="detail-field detail-full">
              <span class="detail-label">Descripción</span>
              <span class="detail-value wrap">{{ selectedIncident.description || '—' }}</span>
            </div>
            <div class="detail-field detail-full">
              <span class="detail-label">Acción correctiva</span>
              <span class="detail-value wrap">{{ selectedIncident.punishment_description || '—' }}</span>
            </div>
          </div>

          <div class="signature-card">
            <div class="signature-head">
              <h5>Firma de enterado</h5>
              <span v-if="isSigned" class="status-pill status-pill--closed">Firmado</span>
              <span v-else class="status-pill status-pill--review">Pendiente</span>
            </div>

            <div v-if="isSigned" class="signature-view">
              <img :src="selectedIncident.acknowledgement_signature" alt="Firma de enterado" />
            </div>

            <template v-else>
              <p class="signature-hint">Firma para dar por enterado de esta incidencia.</p>
              <div class="signature-box">
                <canvas ref="signatureCanvas" class="signature-canvas"></canvas>
              </div>
              <div class="signature-actions">
                <button class="btn btn-ghost" @click="clearSignature" :disabled="savingSignature">Limpiar</button>
                <button class="btn btn-primary" @click="saveSignature" :disabled="savingSignature">
                  {{ savingSignature ? 'Guardando...' : 'Guardar firma' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
:root{
  --bg: #f6f7f9;
  --surface: #ffffff;
  --surface-2: #fbfbfc;
  --border: #e3e6ea;
  --text: #1f2937;
  --muted: #6b7280;
  --shadow: 0 10px 25px rgba(0,0,0,.08);
  --radius: 16px;
  --radius-sm: 12px;
  --brand: #FFC434;
  --brand-2: rgba(255,196,52,.22);
  --danger: #e53935;
}

*{ box-sizing: border-box; }
html, body{ height: 100%; }
body{
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font-family: system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Ubuntu,"Helvetica Neue",Arial;
}

.nav-bar-operations{
  position: fixed;
  top: 0; left: 0;
  width: 100vw;
  height: 64px;
  background-color: var(--brand);
  z-index: 1000;
}
.logo-button{
  position: absolute;
  height: 80%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  margin: 0;
}

.page{
  padding: 86px 12px 18px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header{
  display: grid;
  gap: 12px;
  margin-bottom: 12px;
}

.titles h2{
  margin: 0;
  font-size: 22px;
  letter-spacing: .2px;
  font-weight: 900;
}
.subtitle{
  margin: 6px 0 0;
  color: var(--muted);
  font-size: 13px;
}

.user-actions{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.welcome{
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.chip{
  width: 34px; height: 34px;
  display: grid; place-items: center;
  border-radius: 12px;
  background: var(--brand-2);
  color: var(--text);
  font-size: 16px;
}
.welcome-text{ line-height: 1.1; }
.welcome-label{ font-size: 11px; color: var(--muted); }
.welcome-name{ font-size: 14px; font-weight: 800; }

.action-buttons{
  display: flex;
  gap: 8px;
}

.btn{
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  padding: 10px 12px;
  border-radius: 14px;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  transition: transform .06s ease, background .15s ease, border-color .15s ease, opacity .15s ease;
}
.btn:hover{ background: var(--surface-2); }
.btn:active{ transform: translateY(1px); }
.btn-ghost{ background: transparent; }
.btn-danger{
  background: rgba(229,57,53,.10);
  border-color: rgba(229,57,53,.28);
  color: #b42318;
}

.card{
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.card-header{
  padding: 12px 12px 10px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--border);
  background: #ffffff;
}

.card-header h3{
  margin: 0;
  font-size: 15px;
  font-weight: 900;
}

.muted{ color: var(--muted); }
.mono{ font-variant-numeric: tabular-nums; }
.wrap{ white-space: pre-wrap; }

.table-wrap{
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table{
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 720px;
}

.table thead th{
  position: sticky;
  top: 0;
  background: #f3f4f6;
  text-align: left;
  font-size: 12px;
  letter-spacing: .3px;
  color: var(--muted);
  padding: 12px;
  border-bottom: 1px solid var(--border);
}

.table tbody td{
  padding: 12px;
  border-bottom: 1px solid rgba(227,230,234,.9);
  vertical-align: top;
  font-size: 13px;
  color: var(--text);
}

.table tbody tr:hover td{
  background: #fafafa;
}

.id-pill{
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255,196,52,.26);
  border: 1px solid rgba(255,196,52,.35);
  color: #111827;
  font-weight: 900;
  letter-spacing: .2px;
}

.status-pill{
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid transparent;
  white-space: nowrap;
}
.status-pill--review{ background: #fef3c7; color: #92400e; border-color: #fcd34d; }
.status-pill--closed{ background: #dcfce7; color: #166534; border-color: #86efac; }

.empty{
  padding: 18px 12px;
  color: var(--muted);
  text-align: center;
}

.pager{
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
}

.inline-pager{
  padding: 0;
  border: 0;
  background: transparent;
}

.pager-label{
  font-size: 13px;
  color: var(--muted);
  white-space: nowrap;
}

.icon-btn{
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;
  flex: 0 0 auto;
}
.icon-btn:disabled{
  opacity: .45;
  cursor: not-allowed;
}
.icon-btn img{
  width: 20px;
  height: 20px;
  object-fit: contain;
  display: block;
}

.sticky-pager{
  position: sticky;
  bottom: 10px;
  margin: 10px 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: rgba(255,255,255,.92);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow);
}

.mobile-list{ display: none; }

.row-click{ cursor: pointer; }
.table tbody tr.row-click:hover td{ background: #fff7df; }
.mobile-item{ cursor: pointer; }

.modal-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 16px;
}
.modal-card{
  width: min(640px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}
.modal-head{
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  background: #fff;
}
.modal-head h4{ margin: 0; font-size: 16px; font-weight: 900; }
.btn-close{
  border: 1px solid var(--border);
  background: #f8f8f8;
  border-radius: 10px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-weight: 800;
}
.modal-body{ padding: 16px; display: grid; gap: 16px; }
.detail-grid{
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.detail-field{ display: grid; gap: 4px; }
.detail-full{ grid-column: 1 / -1; }
.detail-label{
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--muted);
  font-weight: 800;
}
.detail-value{ font-size: 14px; color: var(--text); }
.detail-value.wrap{ white-space: pre-wrap; }

.signature-card{
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 12px;
  background: var(--surface-2);
  display: grid;
  gap: 10px;
}
.signature-head{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.signature-head h5{ margin: 0; font-size: 14px; font-weight: 900; }
.signature-hint{ margin: 0; font-size: 12px; color: var(--muted); }
.signature-box{
  width: 100%;
  height: 180px;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.signature-canvas{ width: 100%; height: 100%; display: block; touch-action: none; }
.signature-actions{ display: flex; gap: 8px; justify-content: flex-end; }
.signature-view{
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  padding: 8px;
  display: flex;
  justify-content: center;
}
.signature-view img{ max-width: 100%; max-height: 200px; object-fit: contain; }
.btn-primary{
  background: linear-gradient(180deg, #FFD36B 0%, #FFC434 100%);
  border-color: rgba(0,0,0,.12);
  color: #151515;
}

@media (max-width: 600px){
  .detail-grid{ grid-template-columns: 1fr; }
}

.totals-bar{
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin: 12px;
  background: #ffffff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}
.totals-item{
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.totals-item .label{
  font-size: 12px;
  color: var(--muted);
}
.totals-item .value{
  font-size: 18px;
  font-weight: 900;
  color: var(--text);
}

@media (max-width: 700px){
  .table-wrap{ display: none; }
  .inline-pager{ display: none; }

  .mobile-list{
    display: grid;
    gap: 10px;
    padding: 12px;
  }
  .mobile-item{
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #ffffff;
    padding: 12px;
    box-shadow: var(--shadow);
  }
  .mobile-top{
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 10px;
  }
  .mobile-top .left{
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  .right-col{
    text-align: right;
    min-width: 120px;
    display: grid;
    gap: 4px;
    justify-items: end;
  }
  .mobile-grid{
    display: grid;
    gap: 10px;
  }
  .mobile-block .label{
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 4px;
    display: block;
  }
  .mobile-empty{
    padding: 14px;
    text-align: center;
    color: var(--muted);
  }
  .totals-bar{
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}
</style>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import SignaturePad from 'signature_pad';

export default {
  name: 'MyIncidents',
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      incidents: [],
      username: '',
      firstName: '',
      lastName: '',
      showDetailModal: false,
      selectedIncident: null,
      signaturePad: null,
      savingSignature: false,
      levelOptions: [
        { value: '1', label: '1 - Leve' },
        { value: '2', label: '2 - Moderada' },
        { value: '3', label: '3 - Grave' },
        { value: '4', label: '4 - Muy Grave' },
      ],
    };
  },
  computed: {
    paginatedIncidents() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.incidents.slice(start, end);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.incidents.length / this.pageSize));
    },
    inReviewCount() {
      return this.incidents.filter((incident) => !this.isIncidentClosed(incident)).length;
    },
    isSigned() {
      return !!(this.selectedIncident && this.selectedIncident.acknowledgement_signature);
    },
  },
  mounted() {
    this.fetchCurrentUser();
    this.fetchMyIncidents();
  },
  methods: {
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    levelLabel(levelValue) {
      const option = this.levelOptions.find((item) => String(item.value) === String(levelValue || ''));
      return option?.label || levelValue || 'N/A';
    },
    isIncidentClosed(incident) {
      return String(incident?.status || '').toLowerCase() === 'cerrada';
    },
    incidentStatusClass(statusValue) {
      return this.isIncidentClosed({ status: statusValue }) ? 'status-pill--closed' : 'status-pill--review';
    },
    formatDate(value) {
      if (!value) return '—';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return String(value);
      return parsed.toLocaleString('es-MX');
    },
    openDetail(incident) {
      this.selectedIncident = incident;
      this.showDetailModal = true;
      this.$nextTick(() => {
        if (!this.isSigned) this.initSignaturePad();
      });
    },
    closeDetail() {
      this.showDetailModal = false;
      this.selectedIncident = null;
      this.signaturePad = null;
    },
    initSignaturePad() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const cssWidth = canvas.clientWidth || canvas.offsetWidth || 300;
      const cssHeight = canvas.clientHeight || 180;
      canvas.width = cssWidth * ratio;
      canvas.height = cssHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      this.signaturePad = new SignaturePad(canvas, {
        throttle: 16,
        penColor: 'black',
        backgroundColor: 'rgba(255,255,255,1)',
      });
    },
    clearSignature() {
      if (this.signaturePad) this.signaturePad.clear();
    },
    async saveSignature() {
      if (!this.selectedIncident?.id) return;
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        Swal.fire('Firma vacía', 'Por favor, firma antes de guardar.', 'warning');
        return;
      }
      const signatureDataUrl = this.signaturePad.toDataURL('image/png');
      try {
        this.savingSignature = true;
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.post(
          `${apiDirectory}/employee-incident/${this.selectedIncident.id}/sign/`,
          { signature: signatureDataUrl },
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        );
        const updatedSignature = response.data?.acknowledgement_signature || signatureDataUrl;
        this.selectedIncident.acknowledgement_signature = updatedSignature;
        const target = this.incidents.find((item) => item.id === this.selectedIncident.id);
        if (target) target.acknowledgement_signature = updatedSignature;
        Swal.fire('¡Listo!', 'Tu firma se guardó correctamente.', 'success');
      } catch (error) {
        const errorMessage = error.response ? (error.response.data.error || JSON.stringify(error.response.data)) : error.message;
        Swal.fire('Error', errorMessage || 'No se pudo guardar la firma.', 'error');
      } finally {
        this.savingSignature = false;
      }
    },
    async fetchMyIncidents() {
      try {
        const apiDirectory = process.env.API_DIRECTORY;
        const response = await axios.get(`${apiDirectory}/my-employee-incidents`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        });
        this.incidents = Array.isArray(response.data?.results) ? response.data.results : [];
      } catch (error) {
        console.error('Failed to fetch incidents:', error);
        this.incidents = [];
      }
    },
    fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY;
      axios.get(`${apiDirectory}/current-user/`, {
        headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
      })
        .then((response) => {
          this.username = response.data.username;
          this.firstName = response.data.first_name;
          this.lastName = response.data.last_name;
        })
        .catch((error) => {
          console.error('There was an error fetching the user data:', error);
        });
    },
    logout() {
      // Also flush the Django session cookie (OAuth consent flow).
      axios.post(`${process.env.API_DIRECTORY}/logout/`).catch(() => {});
      this.$store.commit('setToken', null);
      localStorage.removeItem('authToken');
      this.$router.push({ name: 'UserLogin' });
    },
    goBack() {
      this.$router.push({ name: 'OperatorInterface' });
    },
  },
};
</script>
